import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import { POST } from "../../../services/HTTPServices";
import { alertMessage } from "../../../services/alertService";

import {
  LoadingOverlay,
  LoadingSpinner,
  LoadingSpinnerIcon,
} from "../../../styles/loading";
import { Title } from "../../../styles/OTPVerificationStep";
import {
  FormGroup,
  Label,
  InputField,
  ErrorText,
  PasswordToggle,
  InputIcon,
  FullWidthContainer,
  Input,
} from "../../../styles/PersonalInfoStep";
import { FormContainer } from "../../../styles/login";

import Password from "../../../assets/svgs/LoginSignup/Step1/password.svg";

import { ConfirmButton } from "../../../styles/ForgotPassword";
import { getItem } from "../../../utils/cache/LocalStorage";
import { PasswordValidators, validPassword } from "../../../utils/validators";

interface EnterEmailProps {
  callback?: () => void;
}

const ResetPassword = (props: EnterEmailProps) => {
  const navigate = useNavigate();
  const [submitBtnClick, setSubmitBtnClick] = useState(false);
  const [loading, setLoading] = useState(false);

  const [values, setValues] = useState({ password: "", confirmPassword: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [errors, setErrors] = useState<Record<string, boolean>>({
    password: true,
    confirmPassword: true,
  });

  // Toggle password visibility
  const togglePasswordVisibility = () => setShowPassword(!showPassword);
  const toggleConfirmPasswordVisibility = () =>
    setShowConfirmPassword(!showConfirmPassword);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setValues(prev => ({ ...prev, [name]: value }));
  };

  const validateField = async (prop: keyof typeof values, value: string) => {
    let isValid: boolean = true;

    try {
      switch (prop) {
        case "password": {
          isValid = await validPassword({ firstPassword: value, length: 8 });
          break;
        }
        case "confirmPassword": {
          isValid = await validPassword({
            firstPassword: values.password,
            secondPassword: value,
            length: 8,
          });
          break;
        }
      }
    } catch (err) {
      console.error('Validation error:', err);
      isValid = false;
    }

    return !isValid; // Return true if there's an error
  };

  const handleCopy = (event: any) => {
    event.preventDefault(); // Prevent default copy behavior
  };

  // Add useEffect to handle validation when values change
  React.useEffect(() => {
    const validateForm = async () => {
      const fieldsToValidate = ["password", "confirmPassword"] as const;
      const newErrors = { ...errors };
      let hasChanges = false;

      for (const field of fieldsToValidate) {
        const hasError = await validateField(field, values[field]);
        if (newErrors[field] !== hasError) {
          newErrors[field] = hasError;
          hasChanges = true;
        }
      }

      if (hasChanges) {
        setErrors(newErrors);
      }
    };

    validateForm();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.password, values.confirmPassword]);

  const valid = () => {
    const hasNoErrors = Object.values(errors).every(error => !error);
    return hasNoErrors && values.password && values.confirmPassword;
  };

  const confirm = async () => {
    setSubmitBtnClick(true);
    
    // Check if form is valid using the current errors state
    const isFormValid = valid();

    if (isFormValid) {
      setLoading(true);
      let url = "users/ResetPassword";
      const temp_obj = JSON.parse(getItem("forgot_obj") || "null");
      if (temp_obj) {
        let param = {
          email: temp_obj.email,
          otpCode: temp_obj.otp,
          password: values.password,
        };

        let res: any = await POST(url, param, "user");
        if (res && res.status === 200) {
          alertMessage("Success!", "You created new password successfully.");
          navigate("/login");
          setLoading(false);
        } else {
          if (res.status && res.status === 401) navigate("/unauthorized-page");
          else {
            alertMessage("Error!", res.message);
          }
          setLoading(false);
        }
      }
    }
  };

  return (
    <>
      {loading && (
        <LoadingOverlay>
          <LoadingSpinner>
            <LoadingSpinnerIcon />
          </LoadingSpinner>
        </LoadingOverlay>
      )}
      <FormContainer>
        <Title className="mt-16 mb-16">Enter a new Password</Title>
        {/* Password Field */}
        <FormGroup $hasError={!!errors.password}>
          <InputIcon>
            <img src={Password} alt="Password" />
          </InputIcon>
          <FullWidthContainer>
            <Label>Password</Label>
            <InputField $hasError={!!errors.password}>
              <Input
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="Create a password"
                value={values.password}
                onChange={handleChange}
                $hasError={!!errors.password}
                className="ml-negative-16"
                autoComplete="new-password"
              />
              <PasswordToggle
                onClick={togglePasswordVisibility}
                type="button"
                className="mt-negative-24"
              >
                {showPassword ? (
                  <VisibilityOff className="text-warning" />
                ) : (
                  <Visibility className="text-warning" />
                )}
              </PasswordToggle>
            </InputField>
          </FullWidthContainer>
        </FormGroup>
        {submitBtnClick && errors.password && (
          <ErrorText>
            <PasswordValidators firstPassword={values.password} length={8} />
          </ErrorText>
        )}
        {/* Confirm Password Field */}
        <FormGroup $hasError={!!errors.confirmPassword} className="mb-16">
          <InputIcon>
            <img src={Password} alt="Password" />
          </InputIcon>
          <FullWidthContainer>
            <Label>Confirm your Password</Label>
            <InputField $hasError={!!errors.confirmPassword}>
              <Input
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                placeholder="Confirm your password"
                value={values.confirmPassword}
                onChange={handleChange}
                onCopy={handleCopy}
                onPaste={handleCopy}
                $hasError={!!errors.confirmPassword}
                className="ml-negative-16"
                autoComplete="new-password"
              />
              <PasswordToggle
                onClick={toggleConfirmPasswordVisibility}
                type="button"
                className="mt-negative-24"
              >
                {showConfirmPassword ? (
                  <VisibilityOff className="text-warning" />
                ) : (
                  <Visibility className="text-warning" />
                )}
              </PasswordToggle>
            </InputField>
          </FullWidthContainer>
        </FormGroup>
        {submitBtnClick && (
          <ErrorText>
            {values.confirmPassword && values.confirmPassword?.length > 0 ? (
              <PasswordValidators
                firstPassword={values.password}
                secondPassword={values.confirmPassword}
                length={8}
              />
            ) : (
              <span>Confirm Password is required.</span>
            )}
          </ErrorText>
        )}
        <ConfirmButton disabled={!valid()} onClick={confirm}>
          Confirm Password
        </ConfirmButton>
      </FormContainer>
    </>
  );
};

export default ResetPassword;
